<template>
  <div>
    <v-card
      v-if="rma"
      :color="`primary ${$route.name === 'rmas' ? 'lighten-1' : ''}`"
      class="elevation-1"
    >
      <v-toolbar
        class="py-2"
        :color="`primary ${$route.name === 'rmas' ? 'lighten-1' : ''}`"
        flat
      >
        <v-tooltip v-if="$route.name === 'rma'" bottom>
          <v-toolbar-side-icon slot="activator" @click="backToList" icon>
            <v-icon>view_list</v-icon>
          </v-toolbar-side-icon>
          <span>Return to RMA list</span>
        </v-tooltip>
        <v-toolbar-title>
          <h4 v-if="$route.name === 'rmas'">
            {{ rma.products.length }} product(s)
          </h4>
          <h4 v-else>
            {{ rma.name }}
            <span v-if="$route.name === 'rma'">- {{ rma.saleOrderName }}</span>
          </h4>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <ProcessFlow
          v-if="$route.name === 'rma'"
          :states="states"
          :current-state="rma.state"
          xs12
        ></ProcessFlow>
      </v-toolbar>
      <v-card-text class="pa-0">
        <v-container class="py-0">
          <v-layout row wrap>
            <v-flex xs12 sm6>
              <v-text-field
                color="secondary"
                label="Customer Name"
                class="mx-2"
                v-if="rma"
                autofocus
                :loading="isLoading"
                v-model="rma.customer.name"
                box
              ></v-text-field>
              <v-text-field
                color="secondary"
                label="Email Address"
                class="mx-2"
                append-icon="mail"
                v-if="rma"
                autofocus
                type="email"
                :loading="isLoading"
                v-model="rma.customer.email"
                box
              ></v-text-field>
              <v-text-field
                color="secondary"
                label="Phone Number"
                class="mx-2"
                append-icon="phone"
                mask="phone"
                v-if="rma"
                autofocus
                :loading="isLoading"
                v-model="rma.customer.phone"
                box
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                color="secondary"
                label="Street Address"
                class="mx-2"
                v-if="rma"
                autofocus
                :loading="isLoading"
                v-model="rma.customer.street"
                box
              ></v-text-field>
              <v-text-field
                color="secondary"
                label="Street Address Line 2"
                class="mx-2"
                v-if="rma"
                autofocus
                :loading="isLoading"
                v-model="rma.customer.street2"
                box
              ></v-text-field>
              <v-layout row wrap>
                <v-flex xs12 md4>
                  <v-text-field
                    color="secondary"
                    label="City"
                    class="mx-2"
                    v-if="rma"
                    autofocus
                    :loading="isLoading"
                    v-model="rma.customer.city"
                    box
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-combobox
                    color="secondary"
                    label="State"
                    class="mx-2"
                    v-if="rma"
                    v-model="rma.customer.state"
                    :loading="isLoading"
                    :items="stateSelects"
                    @click="loadStates"
                    item-text="name"
                    item-value="name"
                    :return-object="false"
                    box
                  ></v-combobox>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-text-field
                    color="secondary"
                    label="Zip"
                    class="mx-2"
                    v-if="rma"
                    autofocus
                    :loading="isLoading"
                    v-model="rma.customer.zip"
                    box
                  ></v-text-field>
                </v-flex>
              </v-layout>
              <v-combobox
                color="secondary"
                label="Country"
                class="mx-2"
                v-if="rma"
                v-model="rma.customer.country"
                :loading="isLoading"
                :items="countrySelects"
                @click="loadCountries"
                item-text="name"
                item-value="name"
                :return-object="false"
                cache-items
                box
              ></v-combobox>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs12>
              <v-textarea
                v-model="rma.remarks"
                color="secondary"
                v-if="rma"
                :loading="isLoading"
                label="Remarks"
                class="mx-2"
                box
              ></v-textarea>
            </v-flex>
          </v-layout>
        </v-container>
        <v-list v-if="rma" class="py-0" two-line>
          <template v-for="(product, index) in rma.products">
            <v-divider :key="`divider${index}`"></v-divider>
            <v-list-tile
              :key="`tile${index}`"
              :class="`primary ${index % 2 === 0 ? 'lighten-2' : 'lighten-1'}`"
            >
              <v-list-tile-avatar
                v-if="product.isReturning"
                color="success"
                size="34"
              >
                <v-tooltip bottom>
                  <v-icon slot="activator" style="cursor:help;"
                    >keyboard_return</v-icon
                  >
                  <span>Product is being returned</span>
                </v-tooltip>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ product.quantity }} x {{ product.product }} -
                  {{ product.type }} ({{ product.refundAmount | currency }})
                </v-list-tile-title>
                <v-list-tile-sub-title
                  v-html="
                    `${product.reason || ''} ${
                      product.product && product.reason ? '-' : ''
                    } ${product.remarks || ''}`
                  "
                ></v-list-tile-sub-title>

                <v-list-tile-sub-title
                  v-if="totalTimesReturned.length > 0 && product.isReturning"
                  >This product has been returned
                  {{ totalTimesReturned[index] }} time{{
                    totalTimesReturned[index] > 1 ? "s." : "."
                  }}</v-list-tile-sub-title
                >
                <v-progress-linear
                  v-else-if="
                    totalTimesReturned.length === 0 && product.isReturning
                  "
                  :indeterminate="true"
                  height="3"
                />
              </v-list-tile-content>
            </v-list-tile>
          </template>
        </v-list>
      </v-card-text>
      <v-card-actions v-if="$route.name === 'rmas'" class="px-4">
        <ProcessFlow
          :states="states"
          :current-state="rma.state"
          xs12
        ></ProcessFlow>
      </v-card-actions>
      <v-card-actions v-else class="px-4">
        <v-layout row wrap>
          <v-tooltip bottom>
            <v-btn
              v-if="rma"
              slot="activator"
              color="secondary"
              class="mr-2"
              @click="saveRecord()"
              flat
              large
              icon
            >
              <v-icon>save</v-icon>
            </v-btn>
            <span>Save this record</span>
          </v-tooltip>
          <v-tooltip bottom>
            <a
              slot="activator"
              target="new"
              :href="
                `${odooUrl || `http://localhost:8069`}/web#id=${
                  rma.saleOrderID
                }&view_type=form&model=sale.order&menu_id=246&action=363`
              "
            >
              <v-btn v-if="rma" color="secondary" class="mx-2" flat large icon>
                <v-icon>visibility</v-icon>
              </v-btn>
            </a>
            <span>View the Sales Order in Odoo</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn
              v-if="rma"
              slot="activator"
              color="secondary"
              class="mr-2"
              @click="videoDialog = true"
              flat
              large
              icon
            >
              <v-icon>videocam</v-icon>
            </v-btn>
            <span>Video</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn
              v-if="['waiting', 'received', 'done'].includes(rma.state)"
              slot="activator"
              color="secondary"
              class="mr-2"
              @click="printDialog = true"
              flat
              large
              icon
            >
              <v-icon>print</v-icon>
            </v-btn>
            <span>Print Product Labels</span>
          </v-tooltip>
          <v-spacer></v-spacer>
          <v-layout v-if="actions.length > 0" justify-end>
            <template v-for="(action, index) in actions">
              <template v-if="action.show">
                <v-menu v-if="action.items" offset-y :key="index">
                  <template v-slot:activator="{ on }">
                    <v-tooltip bottom>
                      <v-btn
                        slot="activator"
                        color="secondary"
                        flat
                        large
                        v-on="on"
                        icon
                        class="mx-2"
                      >
                        <v-icon>{{ action.icon }}</v-icon>
                      </v-btn>
                      <span>{{ action.label }}</span>
                    </v-tooltip>
                  </template>
                  <v-list>
                    <v-list-tile
                      v-for="(item, index) in action.items"
                      :key="index"
                      :disabled="item.disabled"
                      @click="action.action({ method: item.val, rma })"
                    >
                      <v-list-tile-title>{{ item.label }}</v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>

                <v-tooltip :key="index" bottom v-else>
                  <v-btn
                    color="secondary"
                    @click="action.action({ rma })"
                    slot="activator"
                    class="mx-2"
                    flat
                    icon
                    large
                  >
                    <v-icon>{{ action.icon }}</v-icon>
                  </v-btn>
                  <span>{{ action.label }}</span>
                </v-tooltip>
              </template>
            </template>
          </v-layout>
        </v-layout>
      </v-card-actions>
    </v-card>

    <Chatter
      v-on:send-clicked="sendAttachment"
      v-if="rma && $route.name === 'rma'"
      collection="rmas"
      :id="rma._id"
      xs12
    ></Chatter>
    <v-dialog v-if="rma" v-model="refundExceptionDialog" :width="dialogWidth">
      <v-card color="primary" v-if="rma.exception">
        <v-card-title>
          <h2>Refund Exception</h2>
          <v-spacer />
          <v-btn icon @click="refundExceptionDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-text-field
            color="secondary"
            label="Transaction ID"
            class="mx-2"
            box
            v-model="rma.exception.transactionID"
          ></v-text-field>
          <v-text-field
            color="secondary"
            label="Reason For Exception"
            class="mx-2"
            box
            v-model="rma.exception.reason"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="refundExceptionDialog = false">Cancel</v-btn>
          <v-btn @click="refundException" color="secondary lighten-1"
            >Submit</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="printDialog" :width="dialogWidth">
      <v-card color="primary">
        <v-card-title>
          <h2>Print Product Labels</h2>
        </v-card-title>
        <v-card-text>
          <v-select
            :items="allPrinters"
            v-model="selectedPrinter"
            color="secondary"
            label="Select Printer"
            box
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click="printLabels"
            class="success"
            :disabled="!selectedPrinter"
            >Print Labels</v-btn
          >
          <v-btn @click="printDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <VideoDialog v-model="videoDialog" :rma="rma" />
  </div>
</template>

<script>
import ProcessFlow from "@/components/app/ProcessFlow";
import Chatter from "@/components/app/chatter";
import VideoDialog from "@/components/rma/VideoDialog";
import CONSTANTS from "@/constants";
import { mapGetters, mapActions } from "vuex";
import Axios from "axios";

export default {
  data() {
    return {
      refundExceptionDialog: false,
      orderWeight: 0,
      address: "",
      videoDialog: false,
      printDialog: false
    };
  },

  components: {
    ProcessFlow,
    Chatter,
    VideoDialog
  },

  watch: {
    rma: {
      async handler(val) {
        if (val) {
          this.rma.updatedBy = this.user.displayName;
          if (this.totalTimesReturned.length < 1) {
            val.products.forEach((product, index) => {
              this.setTotalTimesReturned(product.id, index);
            });
          }
          this.setAddress();
          await this.getWeights();
        }
      }
    }
  },

  destroyed() {
    this.$store.commit("rma/resetTotalTimesReturned");
  },

  async mounted() {
    await this.getAllPrinters();
  },

  computed: {
    ...mapGetters({
      rma: "rma/getRma",
      states: "rma/getStates",
      isLoading: "app/isLoading",
      countrySelects: "countriesAndStates/getCountries",
      stateSelects: "countriesAndStates/getStates",
      getProduct: "product/getProduct",
      totalTimesReturned: "rma/getTotalTimesReturned",
      authorization: "app/getHTTPHeaders",
      user: "app/getUser",
      allPrinters: "repair/getAllPrinters",
      storedPrinter: "repair/getSelectedPrinter"
    }),

    odooUrl() {
      return CONSTANTS.ODOO_URL;
    },

    returningItem() {
      return !!this.rma.products.find(product => product.isReturning);
    },

    disableAction() {
      if (this.orderWeight > 5) return "overweight";
      // else if (this.isPoBox()) return "pobox";
      return false;
    },

    normalShippingLabel() {
      if (this.disableAction === "overweight")
        return "Normal Shipping (> 5LBs)";
      else if (this.disableAction === "pobox")
        return "Normal Shipping (PO Box)";
      return "Normal Shipping";
    },

    actions() {
      return [
        {
          label: "Refund Exception",
          icon: "error",
          show: true,
          states: ["waiting", "received", "done"],
          action: this.setRefundDialog
        },
        {
          label: "Create Inbound Shipment",
          icon: "move_to_inbox",
          show: this.returningItem,
          states: ["created"],
          items: [
            {
              val: "dhl",
              label: "Normal Shipping (< 5LBs)",
              disabled: typeof this.disableAction === "string"
            },
            {
              val: "fedex",
              label: "Fedex (> 5LBs)",
              disabled: this.disableAction == "pobox" || !this.disableAction
            },
            // {
            //   val: "endicia",
            //   label: "Expedited Shipping",
            //   disabled: this.disableAction == "overweight"
            // },
            {
              val: "noLabel",
              label: "No Label"
            }
          ],
          action: this.createInboundShipment
        },
        {
          label: "Mark as Received",
          icon: "attach_money",
          show: true,
          states: ["waiting"],
          action: this.markAsRefunded
        },
        {
          label: "Cancel RMA",
          icon: "cancel",
          show: true,
          states: ["created", "waiting"],
          action: this.cancelRMA
        },
        {
          label: "Mark as Rejected",
          icon: "cancel_schedule_send",
          show: true,
          states: ["created", "waiting", "received"],
          action: this.markAsRejected
        },
        {
          label: "Mark as Done",
          icon: "check",
          show: true,
          states: ["created", "waiting", "received", "rejected"],
          action: this.markAsDone
        }
      ].filter(action => action.states.find(state => state === this.rma.state));
    },

    dialogWidth() {
      if (this.$vuetify.breakpoint.xs) {
        return "100%";
      } else if (this.$vuetify.breakpoint.sm) {
        return "50%";
      } else if (this.$vuetify.breakpoint.md) {
        return "40%";
      }
      return "30%";
    },

    selectedPrinter: {
      get() {
        return this.storedPrinter;
      },
      set(val) {
        return this.$store.commit("repair/setSelectedPrinter", val);
      }
    }
  },

  methods: {
    ...mapActions({
      update: "rma/update",
      createInboundShipment: "picking/createInboundShipment",
      sendLabel: "rma/sendAttachment",
      clearChatter: "chatter/clearChatter",
      loadCountries: "countriesAndStates/loadCountries",
      snackbar: "app/setSnackbar",
      productSearch: "product/search",
      completePicking: "picking/completePicking",
      cancelPurchase: "picking/cancelPO",
      getAllPrinters: "repair/getAllPrinters",
      printProductLabels: "picking/printProductLabels"
    }),

    markAsRefunded() {
      this.rma.state = "received";
      this.completePicking({ purchaseID: this.rma.purchaseID });
      this.update({ id: this.rma._id, updates: this.rma });
      this.$forceUpdate();
    },

    markAsRejected() {
      this.rma.state = "rejected";
      this.rma.completedOn = new Date();
      this.update({ id: this.rma._id, updates: this.rma });
      this.$forceUpdate();
    },

    markAsDone() {
      this.rma.state = "done";
      this.rma.completedOn = new Date();
      this.update({ id: this.rma._id, updates: this.rma });
      this.$forceUpdate();
    },

    cancelRMA() {
      this.rma.state = "cancelled";
      this.rma.completedOn = new Date();
      this.cancelPurchase({ id: this.rma.purchaseID });
      this.update({ id: this.rma._id, updates: this.rma });
      this.$forceUpdate();
    },

    saveRecord() {
      this.loadStates();
      this.update({ id: this.rma._id, updates: this.rma });
      this.$forceUpdate();
    },

    printLabels() {
      this.printProductLabels({
        printer: this.selectedPrinter,
        purchaseID: this.rma.purchaseID
      });
    },

    setRefundDialog() {
      this.refundExceptionDialog = true;
    },

    refundException() {
      if (this.rma.exception.transactionID && this.rma.exception.reason) {
        this.update({ id: this.rma._id, updates: this.rma });
        this.snackbar({
          color: "green",
          text: "Refund Exception Added."
        });
        this.refundExceptionDialog = false;
      } else
        this.snackbar({
          color: "red",
          text: "Both fields are required."
        });
    },

    loadStates() {
      this.$store.dispatch(
        "countriesAndStates/loadStates",
        this.rma.customer.country
      );
    },

    sendAttachment(rma) {
      this.sendLabel(rma);
    },

    backToList() {
      this.clearChatter();
      this.$router.push("/rma");
    },

    async getWeights() {
      let weight = 0;
      let it = {};

      this.rma.products.forEach(product => (it[product.id] = product.quantity));

      let ids = Object.keys(it).map(key => parseInt(key));

      await this.productSearch({
        body: {
          kwargs: {
            product: false,
            id: ids
          }
        }
      });

      this.getProduct.forEach(product => {
        weight += it[product.id] * product.x_weight_lbs;
      });

      this.orderWeight = weight;
    },

    setAddress() {
      this.address = this.rma.customer.street + " " + this.rma.customer.street2;
    },

    isPoBox() {
      const regex = new RegExp(
        /(?:P(?:ost(?:al)?)?[.\-\s]*(?:(?:O(?:ffice)?[.\-s]*)?B(?:ox|in|\b|\d)|o(?:ffice|\b)(?:[-\s]*\d)|code)|box[-\s\b]*\d)/,
        "i"
      );

      if (this.address.match(regex)) return true;
    },

    async setTotalTimesReturned(productID, index) {
      let productURL = `${CONSTANTS.BE_URL}/rma/details/product/${productID}`;
      const { data } = await Axios({
        method: "GET",
        url: productURL,
        headers: this.authorization
      });

      this.$store.commit("rma/setTotalTimesReturned", {
        data: data.total,
        index
      });

      this.$forceUpdate();
    }
  }
};
</script>
